import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Spinner, Card, Dialog, Select, Text, Box, Tooltip, Icon } from 'core/components';
import IdTag from 'app/components/IdTag';
import TargetRenderer from 'app/views/protect/ddos/analyze/components/TargetRenderer';
import { HOUR_IN_SECONDS, DAY_IN_SECONDS, COMPARISON_DIRECTIONS } from '@kentik/ui-shared/alerting/constants';
import AlertSeverityBars from 'app/views/alerting/components/AlertSeverityBars';
import AlertStatusTag from 'app/views/alerting/components/AlertStatusTag';
import ConditionsTags from 'app/views/alerting/policies/PolicyDetails/conditions/ConditionsTags';
import { getTimeRangeAroundAlertStart } from './utils';
import NmsNativeAlertDebugChart from '../nms/NmsNativeAlertDebugChart';
import NmsNativeAlertDimensionRenderer from '../nms/NmsNativeAlertDimensionRenderer';
import AlertDebugChart from './AlertDebugChart';

const TIME_RANGES = [
  { value: HOUR_IN_SECONDS / 4, label: 'Alert Start +/- 15 minutes' },
  { value: HOUR_IN_SECONDS / 2, label: 'Alert Start +/- 30 minutes' },
  { value: HOUR_IN_SECONDS, label: 'Alert Start +/- 1 hour' },
  { value: HOUR_IN_SECONDS * 2, label: 'Alert Start +/- 2 hours' },
  { value: DAY_IN_SECONDS / 2, label: 'Alert Start +/- 12 hours' }
];

@inject('$alerting')
@observer
export default class AlertDebugDialog extends Component {
  static defaultProps = {
    isOpen: false,
    onClose: () => {}
  };

  state = {
    lookbackSeconds: TIME_RANGES[0].value,
    debugData: [],
    loading: true
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData() {
    const { $alerting, alertModel } = this.props;
    const { lookbackSeconds } = this.state;
    const sentBetween = getTimeRangeAroundAlertStart(alertModel.startTime, lookbackSeconds);

    this.setState({ loading: true });
    $alerting
      .fetchDebugAlertData(alertModel, { sentBetween })
      .then((debugData) => this.setState({ debugData, loading: false }))
      .catch(() => this.setState({ loading: false }));
  }

  handleRangeSelect = (value) => {
    this.setState({ lookbackSeconds: value }, this.fetchData);
  };

  get renderFlowAlertIndicators() {
    const { alertModel } = this.props;
    if (alertModel.isNmsPolicy) {
      return null;
    }

    const { threshold } = alertModel;
    if (!threshold) {
      return null;
    }

    const { direction, activate } = threshold;
    const directionLabel = direction === COMPARISON_DIRECTIONS.TO_CURRENT ? 'Baseline → Current' : 'Current → Baseline';

    return (
      <Flex borderTop="thinDotted" p="12px" gap="12px" alignItems="center" justifyContent="space-between">
        <Flex gap="4px" flex="none">
          <Text small fontWeight="bold">
            Compares:
          </Text>
          <Text small>{directionLabel}</Text>
        </Flex>
        <Box>
          {threshold.conditions?.length > 0 && (
            <ConditionsTags threshold={threshold} policy={alertModel.policy.get()} showLabels={false} />
          )}
        </Box>
        {activate && (
          <Text small as="div">
            <b>Activation:</b> {activate.times} {activate.times === 1 ? 'trigger' : 'triggers'} in {activate.timeWindow}{' '}
            {activate.timeUnit}
            {activate.timeWindow !== 1 && 's'}
          </Text>
        )}
      </Flex>
    );
  }

  render() {
    const { debugData, lookbackSeconds, loading } = this.state;
    const { isOpen, onClose, alertModel } = this.props;
    const title = (
      <Flex justifyContent="space-between" mr={1}>
        <Text as="div">
          Debug {alertModel.policy.applicationLabel} Alert - {alertModel?.policyObject?.name || ''}
        </Text>
        <IdTag id={alertModel.id} />
      </Flex>
    );

    const ChartComponent = alertModel.isNmsPolicy ? NmsNativeAlertDebugChart : AlertDebugChart; // was: AlertDebugChart

    return (
      <Dialog
        isOpen={isOpen}
        title={title}
        canOutsideClickClose
        canEscapeKeyClose
        isCloseButtonShown
        onClose={() => onClose()}
        iconName="close"
        width={800}
        minHeight={400}
        pb={0}
        position="fixed"
      >
        <Dialog.Body p="12px">
          <Card>
            <Flex alignItems="center" borderBottom="thinDotted" justifyContent="space-between" width="100%" p="12px">
              <Flex alignItems="center" gap="12px">
                <AlertStatusTag isActive={alertModel.isActive} />
                <AlertSeverityBars severity={alertModel.severity} showLabel />
              </Flex>

              <Flex gap="6px" alignItems="center">
                {debugData && loading && <Spinner size={16} />}
                {alertModel.isDataRetentionExpired && (
                  <Tooltip content="Trigger data older than 90 days may no longer be available.">
                    <Icon icon="warning-sign" color="warning" />
                  </Tooltip>
                )}
                <Select
                  options={TIME_RANGES}
                  values={lookbackSeconds}
                  onChange={this.handleRangeSelect}
                  menuWidth="auto"
                  width="auto"
                  minimal
                  small
                />
              </Flex>
            </Flex>
            <Flex flex={1} minWidth={0} p="12px" gap={1} alignItems="center" borderBottom="thinDotted" mb={1}>
              <Text small fontWeight="bold">
                Dimensions:
              </Text>
              {alertModel.isNmsPolicy ? (
                <NmsNativeAlertDimensionRenderer maxDimensionsToShow={3} alertModel={alertModel} small inline />
              ) : (
                <TargetRenderer model={alertModel} showAll inline small />
              )}
            </Flex>
            <ChartComponent data={debugData} alertModel={alertModel} lookbackSeconds={lookbackSeconds} />

            {this.renderFlowAlertIndicators}
          </Card>
        </Dialog.Body>
      </Dialog>
    );
  }
}
